import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Badge,
  ListItemAvatar,
  Backdrop,
  CircularProgress,
  Skeleton,
} from '@mui/material'
import {
  ForumOutlined as ForumIcon,
  StorefrontOutlined as StorefrontIcon,
  Reply as ReplyIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Person as PersonIcon,
  Edit as EditIcon,
} from '@mui/icons-material'
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid'
import { format, isToday, parseISO } from 'date-fns'
import { useSearchParams, useNavigate, useOutletContext } from 'react-router-dom'

import { useNotification } from '../contexts/NotificationContext'
import { useSMBContext } from '../contexts/SMBContext'
import { conversationsApi } from '../services/api'
import ConversationDetailDialog from '../components/dialogs/ConversationDetailDialog'
import SMBSelector from '../components/SMBSelector'
import { useEditLeadDialog } from '../hooks/leads/useEditLeadDialog'
import MessageContent from '../components/dialogs/MessageContent'
import { isPhoneNumber } from '../utils/textParser'
import PageBanner from '../components/PageBanner'
import EmptyState from '../components/EmptyState'
import { ListSkeleton, TableSkeleton } from '../components/skeletons'

interface Conversation {
  id: string
  channel_id: string
  lead_name: string
  lead_phone: string
  lead_id?: string
  lead_status?: string
  notes?: string
  custom_fields?: Record<string, any>
  channel: 'whatsapp' | 'instagram'
  last_message: string
  last_message_time: string
  status: 'active' | 'pending' | 'resolved' | 'escalated' | 'inactive'
  unread_count: number
  is_automated: boolean
  smb_id: string
}

const Conversations: React.FC = () => {
  const navigate = useNavigate()
  const outletContext = useOutletContext<{ smbNavState?: { isOnboarded: boolean; hasSubscription: boolean } } | null>()
  const hasSubscription = outletContext?.smbNavState?.hasSubscription
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { showNotification } = useNotification()
  const { smbs, selectedSMBId, setSelectedSMBId, loading: smbsLoading } = useSMBContext()
  const [loading, setLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [loadingDeepLink, setLoadingDeepLink] = useState(false)
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [summary, setSummary] = useState({
    total_conversations: 0,
    active_conversations: 0,
    pending_conversations: 0,
    resolved_conversations: 0,
    total_unread: 0,
  })
  const [conversationDialogOpen, setConversationDialogOpen] = useState(false)
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [sortUnreadFirst, setSortUnreadFirst] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const loadConversations = useCallback(async (page: number = 1, isRefresh: boolean = false) => {
    if (!selectedSMBId) {
      setConversations([])
      setTotal(0)
      setSummary({
        total_conversations: 0,
        active_conversations: 0,
        pending_conversations: 0,
        resolved_conversations: 0,
        total_unread: 0,
      })
      setLoading(false)
      setIsRefreshing(false)
      return
    }

    try {
      if (isRefresh) {
        setIsRefreshing(true)
      } else {
        setLoading(true)
      }
      const [conversationsResponse, summaryResponse] = await Promise.all([
        conversationsApi.getConversations({ 
          limit: 50, 
          page: page,
          smb_id: selectedSMBId,
          search: searchQuery || undefined,
          sort_unread_first: sortUnreadFirst,
        }),
        conversationsApi.getConversationsSummary(selectedSMBId),
      ])

      const safeConversations = Array.isArray(conversationsResponse?.conversations)
        ? conversationsResponse.conversations
        : []

      console.log('Conversations API Response:', {
        total: conversationsResponse?.total,
        count: safeConversations.length,
        sortUnreadFirst,
        searchQuery,
        conversations: safeConversations
      })

      setConversations(safeConversations)
      setTotal(conversationsResponse?.total ?? 0)
      setCurrentPage(page)

      setSummary({
        total_conversations: summaryResponse?.total_conversations ?? 0,
        active_conversations: summaryResponse?.active_conversations ?? 0,
        pending_conversations: summaryResponse?.pending_conversations ?? 0,
        resolved_conversations: summaryResponse?.resolved_conversations ?? 0,
        total_unread: summaryResponse?.total_unread ?? 0,
      })
    } catch (error) {
      console.error('Error loading conversations:', error)
      showNotification('Failed to load conversations', 'error')
    } finally {
      setLoading(false)
      setIsRefreshing(false)
    }
  }, [selectedSMBId, searchQuery, sortUnreadFirst, showNotification])

  const { openEditLead, renderEditLeadDialog } = useEditLeadDialog({
    onSuccess: (updatedLead) => {
      if (updatedLead) {
        setConversations((prev) =>
          prev.map((conv) =>
            conv.lead_id === updatedLead.id
              ? {
                  ...conv,
                  lead_name: updatedLead.name || conv.lead_name,
                  lead_status: updatedLead.lead_status || updatedLead.status || conv.lead_status,
                  notes: updatedLead.notes !== undefined ? updatedLead.notes : conv.notes,
                  custom_fields: updatedLead.custom_fields || conv.custom_fields,
                }
              : conv
          )
        )
      }
      loadConversations(currentPage, true)
    },
    smbId: selectedSMBId
  })

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput)
      setCurrentPage(1) // Reset to page 1 when search changes
    }, 300)
    
    return () => clearTimeout(timer)
  }, [searchInput])

  // Load conversations from API when dependencies change
  useEffect(() => {
    if (selectedSMBId) {
      loadConversations(currentPage)
    }
  }, [loadConversations, currentPage, selectedSMBId])

  useEffect(() => {
    const handler = (event: Event) => {
      const { conversationId, unreadCount, lastMessageTime } = (event as CustomEvent<{ conversationId?: string; unreadCount?: number; lastMessageTime?: string }>).detail || {}

      if (!conversationId) {
        loadConversations(1, true)
        return
      }

      setConversations((prev) =>
        prev.map((conversation) =>
          conversation.id === conversationId
            ? {
                ...conversation,
                unread_count: typeof unreadCount === 'number' ? unreadCount : conversation.unread_count,
                last_message_time: lastMessageTime || conversation.last_message_time,
              }
            : conversation
        )
      )

      loadConversations(1, true)
    }

    window.addEventListener('conversations-refresh', handler)
    return () => window.removeEventListener('conversations-refresh', handler)
  }, [loadConversations])

   const clearQueryParam = useCallback((key: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete(key)
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  // Handle deep-linking from notifications (conversationId query param)
  useEffect(() => {
    const conversationId = searchParams.get('conversationId')
    if (conversationId && !loading && !loadingDeepLink) {
      if (conversationDialogOpen && conversationId === selectedConversationId) {
        // Already open, just clear parameter
        clearQueryParam('conversationId')
        return
      }

      const openDeepLink = async () => {
        try {
          setLoadingDeepLink(true)
          // Find in existing list
          let targetConversation = conversations.find(c => c.id === conversationId)
          
          if (!targetConversation) {
            // If not in list, fetch details
            const convData = await conversationsApi.getConversation(conversationId)
            if (convData) {
              // Add to list or just set as selected
              targetConversation = convData
              // Ensure we have the correct SMB selected for this conversation
              if (convData.smb_id && convData.smb_id !== selectedSMBId) {
                setSelectedSMBId(convData.smb_id)
              }
            }
          }
          
          if (targetConversation) {
            setSelectedConversationId(conversationId)
            setConversationDialogOpen(true)
            // Clear param so it doesn't re-open on refresh
            clearQueryParam('conversationId')
          }
        } catch (err) {
          showNotification("Failed to open this conversation", 'error')
          console.error('Failed to handle deep link:', err)
        } finally {
          setLoadingDeepLink(false)
        }
      }
      
      openDeepLink()
    }
  }, [searchParams, conversations, loading, conversationDialogOpen, loadingDeepLink, clearQueryParam, selectedSMBId, showNotification, selectedConversationId])

  const getChannelColor = (channel: string) => {
    return channel === 'whatsapp' ? '#25D366' : '#E4405F'
  }

  const getChannelIcon = (channel: string) => {
    return channel === 'whatsapp' ? '💬' : '📷'
  }

  const getStatusColor = (status: string) => {
    const colors = {
      active: 'success',
      pending: 'warning',
      resolved: 'default',
      inactive: 'default',
      escalated: 'info',
    }
    return colors[status as keyof typeof colors] || 'default'
  }

  const parseDate = (value: string) => {
    if (!value) return null
    const normalised = value.includes('T') ? value : value.replace(' ', 'T')
    const iso = /Z|[+-]\d\d:?\d\d$/.test(normalised) ? normalised : `${normalised}Z`
    const parsed = parseISO(iso)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  const formatTime = (dateString: string) => {
    const date = parseDate(dateString)
    if (!date) return '—'
    if (isToday(date)) {
      return format(date, 'HH:mm')
    }
    return format(date, 'MMM dd, HH:mm')
  }

  const handleReply = useCallback((id: string) => {
    setSelectedConversationId(id)
    setConversationDialogOpen(true)
  }, [])

  const handleCloseConversationDialog = useCallback(() => {
    setConversationDialogOpen(false)
    setSelectedConversationId(null)
  }, [])

  const columns: GridColDef[] = [
    {
      field: 'lead_name',
      headerName: 'Contact',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
            {
              !params.value || isPhoneNumber(params.value) ? ( 
                  <PersonIcon sx={{ fontSize: '1.2rem' }} />
              ):(
                params.value.charAt(0).toUpperCase()
              )
            }
          </Avatar>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" fontWeight="medium" noWrap title={params.value || ''} sx={{ maxWidth: '140px' }}>
              {params.value}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap title={params.row.lead_phone ? (params.row.lead_phone.startsWith('+') ? params.row.lead_phone : `+${params.row.lead_phone}`) : ''} sx={{ display: 'block', maxWidth: '140px' }}>
              {params.row.lead_phone ? (params.row.lead_phone.startsWith('+') ? params.row.lead_phone : `+${params.row.lead_phone}`) : ''}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'channel',
      headerName: 'Channel',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              bgcolor: getChannelColor(params.value),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
            }}
          >
            {getChannelIcon(params.value)}
          </Box>
          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'last_message',
      headerName: 'Last Message',
      width: 300,
      renderCell: (params) => (
        <Box>
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 280,
            }}
          >
            <MessageContent text={params.value || ''} lastMessagePreview={true} />
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value) as any}
          size="small"
          variant="outlined"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'unread_count',
      headerName: 'Unread',
      width: 80,
      renderCell: (params) =>
        params.value > 0 ? (
          <Chip
            label={params.value}
            color="error"
            size="small"
            sx={{ minWidth: 24, height: 20 }}
          />
        ) : null,
    },
    {
      field: 'last_message_time',
      headerName: 'Time',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {formatTime(params.value)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params) => {
        const actions = [];
        if (params.row.lead_id) {
          actions.push(
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit Lead"
              onClick={() => openEditLead(
                params.row.lead_id as string,
                params.row.lead_name,
                params.row.lead_phone,
                undefined,
                params.row.lead_status,
                'edit',
                undefined,
                params.row.notes,
                params.row.custom_fields
              )}
            />
          );
        }
        actions.push(
          <GridActionsCellItem
            icon={<ReplyIcon />}
            label="Reply"
            onClick={() => handleReply(params.id as string)}
          />
        );
        return actions;
      },
    },
  ]

  const selectedSMB = smbs.find((s) => s.id === selectedSMBId);
  const smbName = selectedSMB?.name || 'Business';

  return (
    <Box>
      {/* Page Header */}
      <PageBanner
        title="Conversations"
        subtitle="Manage your messages and lead interactions"
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end', height: 40 }}>
            {loading ? (
              <>
                <Skeleton
                  variant="rounded"
                  width={84}
                  height={26}
                  sx={{ bgcolor: 'rgba(255, 255, 255, 0.25)', borderRadius: '13px' }}
                />
                <Skeleton
                  variant="rounded"
                  width={84}
                  height={26}
                  sx={{ bgcolor: 'rgba(255, 255, 255, 0.25)', borderRadius: '13px' }}
                />
              </>
            ) : (
              <>
                <Chip
                  label={`Active: ${summary.active_conversations}`}
                  size="small"
                  sx={{
                    bgcolor: 'white',
                    color: 'success.dark',
                    fontWeight: 'bold',
                    height: 26,
                    '& .MuiChip-label': {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 1.25,
                      py: 0,
                      lineHeight: 1,
                    },
                  }}
                />
                <Chip
                  label={`Unread: ${summary.total_unread}`}
                  size="small"
                  sx={{
                    bgcolor: 'white',
                    color: 'error.main',
                    fontWeight: 'bold',
                    height: 26,
                    '& .MuiChip-label': {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 1.25,
                      py: 0,
                      lineHeight: 1,
                    },
                  }}
                />
              </>
            )}
          </Box>
        }
        onRefresh={() => loadConversations(currentPage, true)}
        refreshing={isRefreshing}
      />

      {/* SMB Selection */}
      <SMBSelector
        smbs={smbs}
        selectedSMBId={selectedSMBId}
        loading={smbsLoading}
        onChange={(value) => {
          setSelectedSMBId(value)
          setCurrentPage(1)
          setSearchInput('')
          setSearchQuery('')
          setConversations([])
          setTotal(0)
        }}
      />

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems={isMobile ? "stretch" : "center"}>
            <TextField
              placeholder="Search by name or phone..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                sx: { height: 40, borderRadius: 1.5 }
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={sortUnreadFirst ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon fontSize="small" />}
                onClick={() => {
                  setSortUnreadFirst(!sortUnreadFirst)
                  setCurrentPage(1)
                }}
                fullWidth={isMobile}
                sx={{
                  height: 40,
                  borderRadius: 1.5,
                  px: 2.5,
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: sortUnreadFirst ? '0 2px 6px rgba(59, 130, 246, 0.3)' : 'none',
                  borderColor: sortUnreadFirst ? 'primary.main' : 'divider',
                }}
              >
                Unread First
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Conversations List/Table */}
      <Card sx={{ mb: isMobile ? 8 : 0 }}>
        <CardContent sx={{ p: 0 }}>
          {isMobile ? (
            smbsLoading || (loading && !isRefreshing && selectedSMBId) ? (
              <ListSkeleton count={6} />
            ) : (smbs.length === 0 || (smbs.length === 1 && !smbs[0].onboarding_completed) || (selectedSMBId && !smbs.find((s) => s.id === selectedSMBId)?.onboarding_completed)) ? (
              hasSubscription === false ? (
                <EmptyState
                  icon={<StorefrontIcon />}
                  title="Subscription Required"
                  description="An active plan is required to access Conversations. Choose a plan to start receiving customer conversations."
                  action={
                    <Button variant="contained" onClick={() => navigate('/dashboard')}>
                      Choose Plan
                    </Button>
                  }
                  minHeight={320}
                  compact
                />
              ) : (
                <EmptyState
                  icon={<StorefrontIcon />}
                  title="Add a Business to Get Started"
                  description="You haven't added a business yet. Connect your WhatsApp business to start receiving customer conversations."
                  action={
                    <Button variant="contained" onClick={() => navigate('/dashboard')}>
                      Add Business
                    </Button>
                  }
                  minHeight={320}
                  compact
                />
              )
            ) : !selectedSMBId ? (
              <EmptyState
                icon={<StorefrontIcon />}
                title="No Business Selected"
                description="Please select a business from the dropdown above to view conversations."
                minHeight={320}
                compact
              />
            ) : conversations.length === 0 ? (
              <EmptyState
                icon={<ForumIcon />}
                title={searchQuery || sortUnreadFirst ? "No matching conversations" : "No conversations yet"}
                description={
                  searchQuery || sortUnreadFirst
                    ? "No conversations match your active search or filter. Try clearing your filters."
                    : "When customers message your business via WhatsApp, your conversations will appear here."
                }
                minHeight={320}
                compact
              />
            ) : (
              <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {conversations.map((conv, index) => (
                  <React.Fragment key={conv.id}>
                    <ListItemButton 
                      onClick={() => handleReply(conv.id)}
                      sx={{ py: 2 }}
                    >
                      <ListItemAvatar>
                        <Badge
                          color="error"
                          badgeContent={conv.unread_count}
                          invisible={conv.unread_count === 0}
                        >
                          <Avatar>
                            {!conv.lead_name || isPhoneNumber(conv.lead_name) ? (
                              <PersonIcon />
                            ) : (
                              conv.lead_name.charAt(0).toUpperCase()
                            )}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
                              <Typography variant="subtitle1" fontWeight="bold" noWrap title={conv.lead_name || ''} sx={{ flexShrink: 1, maxWidth: '140px' }}>
                                {conv.lead_name}
                              </Typography>
                              <Chip
                                label={conv.status}
                                color={getStatusColor(conv.status) as any}
                                size="small"
                                variant="outlined"
                                sx={{ textTransform: 'capitalize', height: 16, fontSize: '0.6rem', flexShrink: 0 }}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                              {formatTime(conv.last_message_time)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="text.primary" noWrap>
                              {conv.last_message || 'No messages'}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                    {index < conversations.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )
          ) : (
            <Box sx={{ height: 600, width: '100%' }}>
              {(smbs.length === 0 || (smbs.length === 1 && !smbs[0].onboarding_completed) || (selectedSMBId && !smbs.find((s) => s.id === selectedSMBId)?.onboarding_completed)) && !smbsLoading ? (
                hasSubscription === false ? (
                  <EmptyState
                    icon={<StorefrontIcon />}
                    title="Subscription Required"
                    description="An active plan is required to access Conversations. Choose a plan to start receiving customer conversations."
                    action={
                      <Button variant="contained" onClick={() => navigate('/dashboard')}>
                        Choose Plan
                      </Button>
                    }
                    minHeight={400}
                  />
                ) : (
                  <EmptyState
                    icon={<StorefrontIcon />}
                    title="Add a Business to Get Started"
                    description="You haven't added a business yet. Connect your WhatsApp business to start receiving customer conversations."
                    action={
                      <Button variant="contained" onClick={() => navigate('/dashboard')}>
                        Add Business
                      </Button>
                    }
                    minHeight={400}
                  />
                )
              ) : !selectedSMBId && !smbsLoading ? (
                <EmptyState
                  icon={<StorefrontIcon />}
                  title="No Business Selected"
                  description="Please select a business from the dropdown above to view conversations."
                  minHeight={400}
                />
              ) : (
                <DataGrid
                  rows={conversations}
                  columns={columns}
                  loading={(loading && !isRefreshing) || smbsLoading}
                  paginationMode="server"
                  sortingMode="server"
                  rowCount={total}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 50,
                      },
                    },
                  }}
                  hideFooter
                  disableRowSelectionOnClick
                  slots={{
                    loadingOverlay: () => <TableSkeleton variant="conversations" />,
                    noRowsOverlay: () => (
                      <EmptyState
                        icon={<ForumIcon />}
                        title={searchQuery || sortUnreadFirst ? "No matching conversations" : "No conversations yet"}
                        description={
                          searchQuery || sortUnreadFirst
                            ? "No conversations match your active search or filter. Try clearing your filters."
                            : "When customers message your business via WhatsApp, your conversations will appear here."
                        }
                        minHeight={400}
                      />
                    ),
                  }}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid rgba(224, 224, 224, 1)',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f9fafb',
                      borderBottom: '2px solid rgba(224, 224, 224, 1)',
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '& .MuiDataGrid-row': {
                      cursor: 'pointer',
                    },
                  }}
                  onRowClick={(params) => handleReply(params.id as string)}
                />
              )}
            </Box>
          )}
        </CardContent>
        
        {/* Manual Pagination Controls */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 2, 
          gap: 2,
          borderTop: '1px solid rgba(224, 224, 224, 1)' 
        }}>
          <Typography variant="body2" color="text.secondary">
            {total > 0 ? (
              <>
                Showing {((currentPage - 1) * 50) + 1}-{Math.min(currentPage * 50, total)} of {total} conversations
                {searchQuery && ` (filtered by "${searchQuery}")`}
              </>
            ) : (
              ''
            )}
          </Typography>
          <Stack direction="row" spacing={1} width={isMobile ? '100%' : 'auto'} justifyContent={isMobile ? 'space-between' : 'flex-end'}>
            <Button
              variant="outlined"
              size="small"
              fullWidth={isMobile}
              disabled={currentPage === 1 || loading}
              onClick={() => {
                const newPage = currentPage - 1
                setCurrentPage(newPage)
                loadConversations(newPage)
              }}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              size="small"
              fullWidth={isMobile}
              disabled={currentPage * 50 >= total || loading}
              onClick={() => {
                const newPage = currentPage + 1
                setCurrentPage(newPage)
                loadConversations(newPage)
              }}
            >
              Next
            </Button>
          </Stack>
        </Box>
      </Card>

      {/* Conversation Detail Dialog */}
      <ConversationDetailDialog
        open={conversationDialogOpen}
        onClose={handleCloseConversationDialog}
        conversationId={selectedConversationId}
        smbId={selectedSMBId}
        smbName={smbName}
      />
      {renderEditLeadDialog()}

      {/* Loading overlay for deep link fetching */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loadingDeepLink}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  )
}

export default Conversations
